/**
 * The edit actions every entity menu offers (Cut, Copy, Paste, Duplicate,
 * Delete, in Unreal's order), built in one place so the Outliner, the viewport
 * and the Edit menu can't drift apart. Each acts on the selection an action
 * takes (selectionEdits' `actionTargets` rule), and is null when it has
 * nothing to act on.
 */
import { useCallback } from "react";
import type { CommandOrigin } from "../../commands/types";
import { useEntityClipboard } from "../clipboard/useEntityClipboard";
import { duplicateEdit } from "../create/entityCopies";
import { runEdit, useEditor } from "../EditorContext";
import type { MenuItem } from "../ui/Menu";
import type { SelectionState } from "./Selection";
import { deleteEdit } from "./selectionEdits";
import type { Edit } from "./selectionEdits";

export interface EntityEdits {
  readonly cut: (() => void) | null;
  readonly copy: (() => void) | null;
  /** Always offered: what the clipboard holds is only known once it's read. */
  readonly paste: () => void;
  readonly duplicate: (() => void) | null;
  readonly remove: (() => void) | null;
}

/** The menu entries for the edits, with their shortcuts; missing ones are disabled. */
export function entityEditItems(edits: EntityEdits): MenuItem[] {
  const item = (label: string, shortcut: string, run: (() => void) | null): MenuItem => ({
    label,
    shortcut,
    disabled: run === null,
    run: () => {
      run?.();
    },
  });
  return [
    item("Cut", "Ctrl+X", edits.cut),
    item("Copy", "Ctrl+C", edits.copy),
    item("Paste", "Ctrl+V", edits.paste),
    item("Duplicate", "Ctrl+D", edits.duplicate),
    item("Delete", "Del", edits.remove),
  ];
}

/** Builds the edits for what a menu acts on (`acting`), when the menu opens. */
export function useEntityEdits(origin: CommandOrigin): (acting: SelectionState) => EntityEdits {
  const { components, world, dispatch, dispatchAll } = useEditor();
  const clipboard = useEntityClipboard(origin);

  return useCallback(
    (acting: SelectionState) => {
      const snapshot = world.getSnapshot();
      const running = (edit: Edit | null) =>
        edit &&
        (() => {
          runEdit({ dispatch, dispatchAll }, edit);
        });
      const copyable = clipboard.canCopy(acting.ids);

      return {
        cut: copyable
          ? () => {
              clipboard.cut(acting);
            }
          : null,
        copy: copyable
          ? () => {
              clipboard.copy(acting.ids);
            }
          : null,
        paste: () => {
          void clipboard.paste();
        },
        duplicate: running(duplicateEdit(snapshot, components, acting.ids, origin)),
        remove: running(deleteEdit(snapshot.entities, acting, acting.ids, origin)),
      };
    },
    [clipboard, components, world, dispatch, dispatchAll, origin],
  );
}
